import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';

interface ConfirmDialogProps {
	open: boolean;
	title: string;
	description: string;
	confirmLabel: string;
	/** Red confirm button, for the actions that cannot be undone. */
	destructive?: boolean;
	isPending: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Confirmation for an action that needs one (Figma 3741:15579), e.g. deleting a social agent rule or
 * a Sales Agent nudge.
 *
 * The caller owns both the open state and the mutation, so cancelling can never leave the item
 * looking changed: nothing is written until `onConfirm` resolves.
 */
export const ConfirmDialog = ({
	open,
	title,
	description,
	confirmLabel,
	destructive,
	isPending,
	onConfirm,
	onCancel
}: ConfirmDialogProps) => (
	<AlertDialog
		open={open}
		onOpenChange={(next) => {
			// Escape and the overlay both arrive here. Refusing them mid-flight stops the dialog
			// disappearing while its write is still in the air.
			if (!next && !isPending) onCancel();
		}}
	>
		{/*
		 * Figma 3741:15579. The padding is per BLOCK, not one value on the shell: the copy sits in
		 * pt-4 px-5 pb-5 and the footer in p-3. A blanket p-5 gave the buttons the same generous
		 * inset as the text and pushed the dialog taller than it needed to be.
		 */}
		<AlertDialogContent className='max-w-[400px] gap-0 border-gray-500 p-0'>
			<AlertDialogHeader className='gap-3 px-5 pt-4 pb-5'>
				{/* H3/Medium — weight 500, not 600. */}
				<AlertDialogTitle className='text-[16px] font-medium leading-6 text-ink-strong'>{title}</AlertDialogTitle>
				<AlertDialogDescription className='text-[13px] font-normal leading-[18px] text-ink'>
					{description}
				</AlertDialogDescription>
			</AlertDialogHeader>

			<AlertDialogFooter className='flex-row items-center justify-end gap-1.5 p-3'>
				<AlertDialogCancel disabled={isPending} className='h-8 px-4 text-[13px] font-normal shadow-none'>
					Cancel
				</AlertDialogCancel>
				<AlertDialogAction
					disabled={isPending}
					onClick={(event) => {
						// Radix closes on any Action click. Holding it open lets the pending state show and
						// leaves the caller to close once the mutation settles.
						event.preventDefault();
						onConfirm();
					}}
					className={cn(
						'h-8 px-4 text-[13px] font-normal shadow-none',
						// surface/error is red-500; the shared --color-destructive is red-600, one step darker.
						destructive && 'border-red-500 bg-red-500 text-white hover:bg-red-500/90'
					)}
				>
					{isPending && <Loader2 className='size-3.5 animate-spin' />}
					{confirmLabel}
				</AlertDialogAction>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
);
